import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveOrderCurrency } from "../../supabase/functions/_shared/order-currency";
import { formatCents } from "@/lib/checkoutQuote";

/**
 * Display-only storefront currency. Same source of truth as the order quote/checkout:
 * store_settings.default_currency resolved by the server rule `resolveOrderCurrency`
 * (null → EUR; only EUR/USD/CAD/GBP). Never guessed from the browser, never a hard-coded symbol.
 */

/** Uppercase ISO code the store's orders are charged in, or null when unsupported. */
export function resolveStoreCurrency(defaultCurrency: string | null | undefined): string | null {
  const c = resolveOrderCurrency(defaultCurrency);
  return c ? c.toUpperCase() : null;
}

/** Accepts only an already-resolved charge currency (any case); anything else is "unknown". */
function knownChargeCurrency(code?: string | null): string | null {
  const c = String(code ?? "").trim();
  if (!c) return null;
  return resolveOrderCurrency(c) ? c.toUpperCase() : null;
}

/**
 * Formats a product/cart amount in the store currency with the checkout formatter
 * (formatCents, fr-FR). Unknown currency (loading, unsupported): amount without any symbol.
 */
export function formatStorePrice(amount: number | string | null | undefined, code?: string | null): string {
  const n = Number(amount ?? 0);
  const value = Number.isFinite(n) ? n : 0;
  const currency = knownChargeCurrency(code);
  const cents = Math.round(value * 100);
  if (!currency) {
    return new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
  }
  return formatCents(cents, currency);
}

/** Store charge currency via the public read-only RPC; null while loading or unsupported. */
export function useStoreCurrency(storeId?: string | null): string | null {
  const { data } = useQuery({
    queryKey: ["store-currency", storeId],
    enabled: !!storeId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.rpc("get_public_store_by_id", { _id: storeId as string });
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return resolveStoreCurrency((row as any)?.default_currency ?? null);
    },
  });
  return data ?? null;
}
